import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, Youtube } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SettingsPanel } from "@/pages/Settings";

const CATEGORIES = ["all", "gaming", "music", "movies", "sports", "news", "tech", "comedy", "podcasts", "entertainment", "cooking", "travel", "fashion", "gardening"];
const REGIONS = ["US", "GB", "CA", "AU", "IN", "DE", "FR", "BR", "JP", "KR", "MX", "ES"];

// Default the feed to the viewer's own region so UK/CA/AU users see local
// trending instead of US. Timezone is the most reliable signal; browser
// language is a fallback.
const detectRegion = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (/^Australia\//.test(tz)) return "AU";
    if (/^(Europe\/(London|Belfast|Jersey|Guernsey|Isle_of_Man))$/.test(tz)) return "GB";
    if (/^America\/(Toronto|Vancouver|Edmonton|Winnipeg|Halifax|St_Johns|Regina|Montreal|Moncton)$/.test(tz)) return "CA";
    if (/^Pacific\/Auckland$/.test(tz)) return "AU"; // nearest supported English region for NZ
  } catch { /* ignore */ }
  try {
    const code = (navigator.language || "").split("-")[1]?.toUpperCase();
    if (REGIONS.includes(code)) return code;
  } catch { /* ignore */ }
  return "US";
};

export default function Dashboard() {
  const [trend, setTrend] = useState(null);
  const [trendBusy, setTrendBusy] = useState(false);
  const [category, setCategory] = useState("all");
  const [region, setRegion] = useState(detectRegion);
  const [topic, setTopic] = useState("");
  const [period] = useState("week");
  const [visibleCount, setVisibleCount] = useState(12);
  const [tab, setTab] = useState("reactions");
  const [reactingTo, setReactingTo] = useState(null);
  const [needsYt, setNeedsYt] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const loadTrending = async (cat = category, reg = region, q = topic, ch = "", per = period) => {
    setTrendBusy(true);
    setNeedsYt(null);
    setVisibleCount(12);
    try {
      const r = await api.get(`/trending?region=${reg}&category=${cat}&q=${encodeURIComponent(q.trim())}&channel=${encodeURIComponent(ch.trim())}&period=${per}`);
      if (r.data.needs_youtube) { setNeedsYt(r.data.reason || "connect"); setTrend([]); }
      else setTrend(r.data.items);
    } catch (e) {
      setTrend([]);
      toast.error(e.response?.data?.detail || "Trending unavailable right now");
    }
    setTrendBusy(false);
  };
  useEffect(() => { loadTrending(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- initial fetch only

  const reactTo = async (v) => {
    setReactingTo(v.video_id);
    try {
      const res = await api.post("/projects", { youtube_url: v.url, source_title: `${v.title} — ${v.channel}` });
      navigate(`/studio/${res.data.project_id}`);
    } catch {
      toast.error("Could not create project");
      setReactingTo(null);
    }
  };

  const create = (e) => {
    e.preventDefault();
    loadTrending(category, region, topic, "", "week");
  };

  return (
    <AppShell>
      <div className="p-10">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display font-bold text-3xl tracking-tight mb-1 text-zinc-900">{tab === "reactions" ? "Reactions" : "Settings"}</h1>
            <p className="text-zinc-500 text-sm">{tab === "reactions" ? "Search a topic, pick a video, and publish your reaction." : "Connect your YouTube channel to publish your reactions."}</p>
          </div>
          <button onClick={() => setTab(tab === "reactions" ? "settings" : "reactions")} data-testid="settings-tab-btn"
            className="shrink-0 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-[4px] px-5 py-2.5 transition-colors">
            {tab === "reactions" ? "Settings" : "Reactions"}
          </button>
        </div>

        {tab === "settings" ? <SettingsPanel /> : (
        <>
        <form onSubmit={create} className="bg-zinc-50 border border-zinc-200 shadow-sm rounded-[4px] p-6 mb-10">
          <div className="text-sm font-medium mb-3 flex items-center gap-2 text-zinc-900"><Search size={15} className="text-rose-500" /> Search a topic</div>
          <div className="flex gap-3">
            <input value={topic} onChange={(e) => setTopic(e.target.value)}
              placeholder="Search a topic"
              data-testid="topic-search-input"
              className="flex-[2] bg-white border border-zinc-300 rounded-[4px] px-4 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none" />
            <button type="submit" disabled={trendBusy} data-testid="topic-search-btn"
              className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium rounded-[4px] px-6 transition-colors">
              {trendBusy ? "Searching…" : "Search"}
            </button>
          </div>
        </form>

        <section className="mb-10">
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <select value={category} aria-label="Category" data-testid="trending-category-select"
              onChange={(e) => { setCategory(e.target.value); loadTrending(e.target.value, region, topic, "", "week"); }}
              className="bg-white border border-zinc-300 rounded-[4px] px-3 py-1.5 text-xs text-zinc-900 capitalize outline-none focus:ring-2 focus:ring-rose-500">
              {CATEGORIES.map((c) => (
                <option key={c} value={c} className="capitalize">{c}</option>
              ))}
            </select>
            <select value={region} onChange={(e) => { setRegion(e.target.value); loadTrending(category, e.target.value, topic, "", "week"); }} data-testid="trending-region-select"
              className="bg-white border border-zinc-300 rounded-[4px] px-3 py-1.5 text-xs text-zinc-900 outline-none">
              {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {trendBusy ? (
            <div className="text-zinc-500 text-sm flex items-center gap-2 py-6"><Loader2 size={14} className="animate-spin" /> Ranking what's climbing…</div>
          ) : needsYt ? (
            <div className="bg-zinc-50 border border-zinc-200 shadow-sm rounded-[4px] p-8 text-center" data-testid="trending-connect-youtube">
              <Youtube size={32} className="text-rose-600 mx-auto mb-3" />
              <div className="text-sm font-medium text-zinc-900 mb-1">
                {needsYt === "quota" ? "YouTube API limit reached for today" : "Connect YouTube for best results"}
              </div>
              <p className="text-zinc-500 text-xs max-w-md mx-auto mb-4">
                {needsYt === "quota"
                  ? "You've hit today's YouTube Data API quota. Trending will refresh once it resets (midnight Pacific)."
                  : "Trending research pulls the most-viewed videos in each category straight from YouTube's official API. Connect your YouTube account to unlock it."}
              </p>
              {needsYt !== "quota" && (
                <button onClick={() => setTab("settings")} data-testid="trending-connect-btn"
                  className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium rounded-[4px] px-5 py-2 transition-colors">
                  Connect YouTube
                </button>
              )}
            </div>
          ) : !trend?.length ? (
            <div className="text-zinc-500 text-sm py-4">Nothing found — try another category.</div>
          ) : (
            <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trending-grid">
              {trend.slice(0, visibleCount).map((v) => (
                <div key={v.video_id} className="bg-zinc-50 border border-zinc-200 shadow-sm rounded-[4px] overflow-hidden hover:border-zinc-300 hover:shadow-md transition-all group" data-testid={`trending-card-${v.video_id}`}>
                  <img src={v.thumbnail} alt="" className="w-full aspect-video object-cover" loading="lazy" decoding="async"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `https://i.ytimg.com/vi/${v.video_id}/hqdefault.jpg`; }} />
                  <div className="p-3">
                    <div className="text-xs font-medium line-clamp-2 min-h-[32px] text-zinc-900">{v.title}</div>
                    <div className="text-[10px] text-zinc-500 mt-1 truncate">{v.channel} · {v.views}</div>
                    <button onClick={() => reactTo(v)} disabled={reactingTo === v.video_id} data-testid={`react-to-${v.video_id}`}
                      className="mt-2.5 w-full bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-[11px] font-medium rounded-[4px] py-1.5 transition-colors flex items-center justify-center gap-1">
                      {reactingTo === v.video_id ? <Loader2 size={11} className="animate-spin" /> : "React to this"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {trend.length > visibleCount && (
              <div className="flex justify-center mt-8">
                <button onClick={() => setVisibleCount((c) => c + 6)} data-testid="trending-load-more-btn"
                  className="border border-zinc-300 hover:border-zinc-400 text-zinc-800 text-sm font-medium rounded-[4px] px-8 py-2.5 transition-colors">
                  Load more
                </button>
              </div>
            )}
            </>
          )}
        </section>
        </>
        )}
      </div>
    </AppShell>
  );
}
